/**
 * Integration lifecycle statuses — the CLIENT twin of the server's
 * src/config/integrationPlatforms.ts.
 *
 * WHY THIS FILE EXISTS
 * --------------------
 * `Integration.platform` / `Integration.status` are free-form String columns
 * carrying TWO conventions at once. The Meta family (FACEBOOK / INSTAGRAM /
 * WHATSAPP) is written UPPERCASE with status `'active'`; everything else is
 * lowercase with `'connected'`; `metaMarketingService` has long also treated
 * `'enabled'` as live. A reader that pins ONE spelling matches zero rows and
 * the surface fails SILENTLY — no error, no log, just "not connected" forever.
 *
 * The server has guarded this with LIVE_STATUSES + an always-on convention
 * test since the WhatsApp outage (e6526d1f). The client had no such constant,
 * so the same bug re-grew here: the Business Hub's Acquisition card decided
 * "connected" from lowercase platform literals and no status check at all, and
 * told merchants with four live integrations that none of them were connected.
 *
 * HOW TO USE
 * ----------
 *   • "Is this integration usable?" → `isLiveIntegrationStatus(row.status)`,
 *     or `isLiveIntegrationRow(row)` when filtering a GET /integrations list.
 *     Never `status === 'connected'` / `status === 'active'` on its own, and
 *     never a local array of the spellings — that is exactly the drift this
 *     module exists to end (guarded by __tests__/integrationStatusLiterals).
 *   • Platform matching → normalise through PLATFORM_TO_REGISTRY_IDS
 *     (see hooks/useConnectedIntegrations.deriveConnectedIds), which lowercases
 *     first and therefore handles both casing families.
 *   • `NEEDS_REAUTH_STATUS` is NOT live: the token died and the merchant must
 *     reconnect. It must never be collapsed into "Not connected" without the
 *     reconnect affordance (see app/IntegrationReauthBanner).
 */

/** Every status meaning "connected and usable". Mirrors the server list. */
export const LIVE_STATUSES = ['connected', 'active', 'enabled'] as const;

export type LiveIntegrationStatus = (typeof LIVE_STATUSES)[number];

/**
 * "The connection exists but is unusable until the merchant re-authenticates."
 * Written by the Meta token-refresh sweep. Deliberately excluded from
 * LIVE_STATUSES — it is a distinct, surfaceable state, not a live one.
 */
export const NEEDS_REAUTH_STATUS = 'needs_reauth';

/** Case- and whitespace-insensitive: row casing varies by platform family. */
export function isLiveIntegrationStatus(status: string | null | undefined): boolean {
  if (typeof status !== 'string') return false;
  const normalised = status.trim().toLowerCase();
  return (LIVE_STATUSES as readonly string[]).includes(normalised);
}

/**
 * Row-level twin, for filtering a raw GET /integrations payload whose members
 * are typed as `Record<string, unknown>`.
 */
export function isLiveIntegrationRow(row: { status?: unknown }): boolean {
  return isLiveIntegrationStatus(typeof row.status === 'string' ? row.status : null);
}
